import { Component, HostBinding, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ProductHero } from '../../../page/landing-layout/tool-pages/product-hero.data';
import { ProductShots, Shot } from '../../../page/landing-layout/tool-pages/product-shots';
import { featureShots, ProductFeature, ProductScene, ProductStore, sceneShot, sceneSrc, ShowcaseConfig } from '../../../data/product/product-features.data';
import { AnalyticsService } from '../../../service/analytics/analytics.service';
import { storeAppListPrice } from '../../../data/product/product-lookup.data';
import { openInMacAppStore } from '../../../util/mac-app-store';
import { CartPopupService } from '../../../service/cart/cart-popup.service';
import { StoreService } from '../../../service/store/store.service';

const TURN_MS = 8000;

/**
 * A product under the home hero (Dock Town, LangMi): what it is on the left, and on the right
 * what it does, one feature at a time, each with the headline of its App Store picture as text
 * and the picture's scenes as pictures of their own, every one opening in the site's lightbox
 * (ProductShots). Everything it says and shows comes from its ShowcaseConfig
 * (dock-town-features.data.ts, langmi-features.data.ts); wide scenes and phone screens lay out
 * differently on the stage (shape).
 */
@Component( {
  selector: 'app-product-showcase',
  templateUrl: './product-showcase.component.html',
  styleUrls: [ './product-showcase.component.scss' ],
} )
export class ProductShowcaseComponent extends ProductShots implements OnInit, OnDestroy {

  @Input() config!: ShowcaseConfig;

  /** The lightbox's phone rules (tool-lightbox.scss) apply when the product's pictures are phone screens. */
  @HostBinding( 'class.is-phone' ) get isPhone(): boolean {
    return this.config?.shape === 'phone';
  }

  slides: ProductFeature[] = [];

  // ProductShots: no stage picture here, and every scene of every slide in one list, so the
  // lightbox's arrows walk through them all (set once from the config: the arrows go by identity)
  readonly hero = { src: '' } as ProductHero;
  shots: Shot[] = [];

  active = 0;
  /** The slides whose pictures carry a src: the one shown, its neighbours, and every one shown before. */
  readonly loaded = new Set<number>();

  private timer: ReturnType<typeof setInterval> | undefined;
  private preload: ReturnType<typeof setTimeout> | undefined;
  private paused = false;
  private onVisibility = () => this.sync();

  constructor(
    private zone: NgZone,
    private analyticsService: AnalyticsService,
    private storeService: StoreService,
    private cartPopupService: CartPopupService,
  ) {
    super();
  }

  /** a slide of this card shows a second row of buttons while its product is on sale (Add to cart, then its store) */
  get twoRowCard(): boolean {
    return this.slides.some( s => this.secondRow( s ) );
  }

  /** the slide's product is on sale and has a row under Add to cart: its store button, or its Coming Soon note */
  secondRow( slide: ProductFeature ): boolean {
    return this.sellsHere( slide ) && !!( slide.store || slide.soon || this.config.soon );
  }

  /** the slide's product is on sale on the site right now (its sku is in the catalogue with a price) */
  sellsHere( slide: ProductFeature ): boolean {
    return !!slide.cartSku && this.storeService.availability( slide.cartSku ) === 'buy';
  }

  /** "$2.99": the live price, else the app's list price */
  priceOf( slide: ProductFeature ): string | null {
    return slide.cartSku ? this.storeService.priceLabel( slide.cartSku, storeAppListPrice( slide.cartSku ) ) : null;
  }

  /** Add to cart: the mini cart opens (a toast on a phone), the page stays */
  addToCart( slide: ProductFeature ): void {
    if ( slide.cartSku ) this.cartPopupService.add( slide.cartSku, 'home-showcase' );
  }

  /** The store button: the same install event the product's own page logs, marked as from the home. */
  onInstall( store: ProductStore, event?: Event ): void {
    this.analyticsService.logEvent( store.event, { ...store.attributes, from: 'home' } );
    // a Mac App Store listing opens in the App Store app on a Mac
    if ( store.macApp ) openInMacAppStore( event, store.url );
  }

  ngOnInit(): void {
    // a slide shows its home scenes when it has them (the page keeps the plain screens)
    this.slides = this.config.slides.map( f => f.homeScenes ? { ...f, scenes: f.homeScenes, layout: f.homeLayout || f.layout } : f );
    this.shots = featureShots( this.config.pictures, this.slides );
    [ 0, 1, this.slides.length - 1 ].forEach( i => this.loaded.add( i ) );
    document.addEventListener( 'visibilitychange', this.onVisibility );
    this.sync();
    // the other slides' pictures load once the page has settled, so a chip picked early fades in a
    // picture and never a blank
    this.zone.runOutsideAngular( () => {
      this.preload = setTimeout( () => this.zone.run( () => this.slides.forEach( ( _, i ) => this.loaded.add( i ) ) ), 2500 );
    } );
  }

  ngOnDestroy(): void {
    document.removeEventListener( 'visibilitychange', this.onVisibility );
    clearTimeout( this.preload );
    this.stop();
  }

  get slide(): ProductFeature {
    return this.slides[ this.active ];
  }

  src( scene: ProductScene ): string {
    return sceneSrc( this.config.pictures, scene );
  }

  /** The lightbox entry of a scene (the same object as in `shots`, so the arrows know where it is). */
  shotOf( scene: ProductScene ): Shot {
    return this.shots.find( s => s.src === this.src( scene ) ) || sceneShot( this.config.pictures, scene );
  }

  show( index: number ): void {
    this.active = ( index + this.slides.length ) % this.slides.length;
    this.loaded.add( this.active );
    this.loaded.add( ( this.active + 1 ) % this.slides.length );
    this.loaded.add( ( this.active - 1 + this.slides.length ) % this.slides.length );
  }

  /** A pick by the visitor: shown, and the turning starts over from it. */
  pick( index: number ): void {
    this.show( index );
    this.stop();
    this.sync();
  }

  /** A scene clicked: enlarged, and the turning rests while the lightbox is open. */
  enlarge( scene: ProductScene ): void {
    this.openLightbox( this.shotOf( scene ) );
    this.stop();
  }

  override closeLightbox(): void {
    super.closeLightbox();
    this.sync();
  }

  pause( on: boolean ): void {
    this.paused = on;
    this.sync();
  }

  trackSlide( _: number, slide: ProductFeature ): string {
    return slide.key;
  }

  trackScene( _: number, scene: ProductScene ): string {
    return scene.key;
  }

  private sync(): void {
    const run = !this.paused && !this.lightboxOpen && document.visibilityState === 'visible';
    if ( run && !this.timer ) {
      this.zone.runOutsideAngular( () => {
        this.timer = setInterval( () => this.zone.run( () => this.show( this.active + 1 ) ), TURN_MS );
      } );
    } else if ( !run ) {
      this.stop();
    }
  }

  private stop(): void {
    if ( this.timer ) {
      clearInterval( this.timer );
      this.timer = undefined;
    }
  }
}
